import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Users, UserPlus, Mail, Phone, Baby, Copy, Check,
  Search, MoreHorizontal, Trash2, Edit, Eye, UserX, UserCheck, Ban
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import DashboardLayout from '@/components/DashboardLayout';

import API from '@/config/api';
import ParentContactsPanel from '@/components/ParentContactsPanel';
// API imported from config;

const ParentsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [parents, setParents] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [parentToDelete, setParentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [parentToSuspend, setParentToSuspend] = useState(null);
  const [suspendLoading, setSuspendLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [newInvite, setNewInvite] = useState({
    name: '',
    email: '',
    phone: '',
    child_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [parentsRes, childrenRes] = await Promise.all([
        axios.get(`${API}/auth/parents`, getAuthHeaders()),
        axios.get(`${API}/children`, getAuthHeaders())
      ]);
      setParents(parentsRes.data);
      setChildren(childrenRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load parents');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!newInvite.name || !newInvite.email) {
      toast.error('Name and email are required');
      return;
    }

    setInviteLoading(true);
    try {
      const res = await axios.post(
        `${API}/auth/invite-parent`,
        newInvite,
        getAuthHeaders()
      );

      if (res.data.existing) {
        toast.success('Parent already exists. Child has been linked.');
      } else {
        toast.success(`Parent account created for ${newInvite.name}`);
        
        // Show temporary password if email wasn't sent
        if (res.data.temp_password) {
          toast.info(
            <div>
              <p className="font-semibold">Temporary Password:</p>
              <code className="bg-muted px-2 py-1 rounded">{res.data.temp_password}</code>
              <p className="text-xs mt-1">Share this with the parent securely</p>
            </div>,
            { duration: 15000 }
          );
        }
      }

      setInviteOpen(false);
      setNewInvite({ name: '', email: '', phone: '', child_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite parent');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDeleteClick = (parent) => {
    setParentToDelete(parent);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!parentToDelete) return;
    
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/auth/users/${parentToDelete.id}`, getAuthHeaders());
      toast.success(`${parentToDelete.name}'s account deleted successfully`);
      setDeleteDialogOpen(false);
      setParentToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete parent');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSuspendClick = (parent) => {
    setParentToSuspend(parent);
    setSuspendDialogOpen(true);
  };

  const handleConfirmSuspend = async () => {
    if (!parentToSuspend) return;
    
    setSuspendLoading(true);
    const isSuspended = parentToSuspend.is_active === false;
    const endpoint = isSuspended ? 'reactivate' : 'deactivate';
    
    try {
      await axios.put(`${API}/auth/users/${parentToSuspend.id}/${endpoint}`, {}, getAuthHeaders());
      toast.success(
        isSuspended 
          ? `${parentToSuspend.name}'s account has been reactivated`
          : `${parentToSuspend.name}'s account has been suspended`
      );
      setSuspendDialogOpen(false);
      setParentToSuspend(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isSuspended ? 'reactivate' : 'suspend'} account`);
    } finally {
      setSuspendLoading(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedPassword(id);
    setTimeout(() => setCopiedPassword(null), 2000);
  };

  const handleParentClick = (parent) => {
    setSelectedParent(parent);
    setEditForm({
      name: parent.name || '',
      email: parent.email || '',
      phone: parent.phone || '',
      address: parent.address || '',
      emergency_contact: parent.emergency_contact || '',
      emergency_phone: parent.emergency_phone || '',
      notes: parent.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveParent = async () => {
    if (!selectedParent) return;
    setEditLoading(true);
    try {
      await axios.put(
        `${API}/auth/users/${selectedParent.id}`,
        editForm,
        getAuthHeaders()
      );
      toast.success(`${editForm.name}'s information updated`);
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update parent');
    } finally {
      setEditLoading(false);
    }
  };

  const filteredParents = parents.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get children linked to each parent
  const getParentChildren = (parentId) => {
    return children.filter(c => c.parent_id === parentId);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="parents-page">
        {/* Unified parent app-access panel — shows linked + unlinked + signup status */}
        <ParentContactsPanel />

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl flex items-center gap-2">
              <Users className="w-6 h-6 text-primary" />
              Parent Management
            </h1>
            <p className="text-muted-foreground">
              Invite and manage parent accounts for your center
            </p>
          </div>
          
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="invite-parent-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Parent
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite Parent</DialogTitle>
                <DialogDescription>
                  Create a parent account. They will receive login credentials to access the parent portal.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="parent-name">Parent Name *</Label>
                  <Input
                    id="parent-name"
                    placeholder="John Smith"
                    value={newInvite.name}
                    onChange={(e) => setNewInvite({...newInvite, name: e.target.value})}
                    data-testid="parent-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="parent-email">Email Address *</Label>
                  <Input
                    id="parent-email"
                    type="email"
                    placeholder="parent@email.com"
                    value={newInvite.email}
                    onChange={(e) => setNewInvite({...newInvite, email: e.target.value})}
                    data-testid="parent-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="parent-phone">Phone Number</Label>
                  <Input
                    id="parent-phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={newInvite.phone}
                    onChange={(e) => setNewInvite({...newInvite, phone: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="link-child">Link to Child (Optional)</Label>
                  <Select 
                    value={newInvite.child_id || "none"} 
                    onValueChange={(v) => setNewInvite({...newInvite, child_id: v === "none" ? "" : v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a child to link" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No child selected</SelectItem>
                      {children.filter(c => !c.parent_id).map(child => (
                        <SelectItem key={child.id} value={child.id}>
                          {child.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Only showing children without a linked parent
                  </p>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleInvite} 
                  disabled={inviteLoading}
                  className="btn-primary"
                  data-testid="send-invite-btn"
                >
                  {inviteLoading ? 'Sending...' : 'Send Invite'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{parents.length}</p>
                <p className="text-sm text-muted-foreground">Total Parents</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Baby className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {children.filter(c => c.parent_id).length}
                </p>
                <p className="text-sm text-muted-foreground">Children Linked</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Baby className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {children.filter(c => !c.parent_id).length}
                </p>
                <p className="text-sm text-muted-foreground">Children Unlinked</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search parents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-parents-input"
            />
          </div>
        </div>

        {/* Parents Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : filteredParents.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-lg">No Parents Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Invite parents to give them access to the parent portal
                </p>
                <Button onClick={() => setInviteOpen(true)} className="btn-primary">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite First Parent
                </Button>
              </div>
            ) : (
              <Table className="mobile-cards">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Children</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredParents.map((parent) => {
                    const linkedChildren = getParentChildren(parent.id);
                    return (
                      <TableRow key={parent.id} data-testid={`parent-row-${parent.id}`} 
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleParentClick(parent)}
                      >
                        <TableCell className="font-medium">
                          {parent.name}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            {parent.email}
                          </div>
                        </TableCell>
                        <TableCell>
                          {linkedChildren.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {linkedChildren.map(child => (
                                <Badge key={child.id} variant="secondary" className="text-xs">
                                  {child.name}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">No children linked</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {parent.is_active === false ? (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                              Suspended
                            </Badge>
                          ) : parent.must_change_password ? (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                              Pending Setup
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Active
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {parent.created_at 
                            ? new Date(parent.created_at).toLocaleDateString()
                            : 'N/A'
                          }
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                onClick={(e) => { e.stopPropagation(); handleParentClick(parent); }}
                              >
                                <Edit className="w-4 h-4 mr-2 text-blue-600" />
                                <span className="text-blue-600">Edit Information</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={(e) => { e.stopPropagation(); handleSuspendClick(parent); }}
                              >
                                {parent.is_active === false ? (
                                  <>
                                    <UserCheck className="w-4 h-4 mr-2 text-green-600" />
                                    <span className="text-green-600">Reactivate Account</span>
                                  </>
                                ) : (
                                  <>
                                    <Ban className="w-4 h-4 mr-2 text-amber-600" />
                                    <span className="text-amber-600">Suspend Account</span>
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-red-600 focus:text-red-600"
                                onClick={(e) => { e.stopPropagation(); handleDeleteClick(parent); }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Account
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Parent Account</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{parentToDelete?.name}&apos;s</strong> account?
                This action cannot be undone. The parent will no longer be able to access the platform,
                and any linked children will be unlinked from this parent.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Account'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Suspend/Reactivate Confirmation Dialog */}
        <AlertDialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {parentToSuspend?.is_active === false ? 'Reactivate' : 'Suspend'} Parent Account
              </AlertDialogTitle>
              <AlertDialogDescription>
                {parentToSuspend?.is_active === false ? (
                  <>
                    Are you sure you want to reactivate <strong>{parentToSuspend?.name}&apos;s</strong> account?
                    They will be able to log in and access the parent portal again.
                  </>
                ) : (
                  <>
                    Are you sure you want to suspend <strong>{parentToSuspend?.name}&apos;s</strong> account?
                    They will not be able to log in until their account is reactivated.
                    Their data will be preserved.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={suspendLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmSuspend}
                disabled={suspendLoading}
                className={parentToSuspend?.is_active === false 
                  ? "bg-green-600 hover:bg-green-700" 
                  : "bg-amber-600 hover:bg-amber-700"
                }
              >
                {suspendLoading 
                  ? (parentToSuspend?.is_active === false ? 'Reactivating...' : 'Suspending...') 
                  : (parentToSuspend?.is_active === false ? 'Reactivate Account' : 'Suspend Account')
                }
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Parent Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Parent Information</DialogTitle>
              <DialogDescription>
                Update {selectedParent?.name}'s profile details
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Full Name</Label>
                  <Input
                    id="edit-name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    data-testid="edit-parent-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    data-testid="edit-parent-email"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">Phone</Label>
                  <Input
                    id="edit-phone"
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                    data-testid="edit-parent-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-address">Address</Label>
                  <Input
                    id="edit-address"
                    value={editForm.address}
                    onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                    data-testid="edit-parent-address"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-emergency-contact">Emergency Contact</Label>
                  <Input
                    id="edit-emergency-contact"
                    value={editForm.emergency_contact}
                    onChange={(e) => setEditForm({...editForm, emergency_contact: e.target.value})}
                    data-testid="edit-parent-emergency-contact"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-emergency-phone">Emergency Phone</Label>
                  <Input
                    id="edit-emergency-phone"
                    type="tel"
                    value={editForm.emergency_phone}
                    onChange={(e) => setEditForm({...editForm, emergency_phone: e.target.value})}
                    data-testid="edit-parent-emergency-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <Input
                  id="edit-notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Any additional notes..."
                  data-testid="edit-parent-notes"
                />
              </div>

              {/* Linked Children */}
              {selectedParent && (
                <div className="space-y-2">
                  <Label>Linked Children</Label>
                  <div className="flex flex-wrap gap-2">
                    {getParentChildren(selectedParent.id).length > 0 ? (
                      getParentChildren(selectedParent.id).map(child => (
                        <Badge key={child.id} variant="secondary">
                          {child.name}
                        </Badge>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">No children linked</p>
                    )}
                  </div>
                </div>
              )}

              {/* Account Info */}
              {selectedParent && (
                <div className="border-t pt-4 space-y-2">
                  <Label className="text-muted-foreground text-xs uppercase tracking-wider">Account Details</Label>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Status: </span>
                      {selectedParent.is_active === false ? (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Suspended</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>
                      )}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Joined: </span>
                      {selectedParent.created_at ? new Date(selectedParent.created_at).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveParent} 
                disabled={editLoading}
                className="btn-primary"
                data-testid="save-parent-btn"
              >
                {editLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ParentsPage;
